import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MainNavbar } from '../../components/navigation/MainNavbar';
import airportImage from '../../assets/airportt.png';
import { useTheme } from '../../contexts/ThemeContext.jsx';

const languages = [
  { code: 'en', label: 'English' },
  { code: 'fr', label: 'Français' },
];

export default function SettingsPage() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [language, setLanguage] = useState(
    localStorage.getItem('flexy-language') || 'en',
  );
  const { theme, setTheme } = useTheme();

  useEffect(() => {
    localStorage.setItem('flexy-language', language);
    i18n.changeLanguage(language);
  }, [language]);

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900 dark:bg-slate-950 dark:text-slate-100">
      <MainNavbar />

      <section className="relative min-h-[420px] overflow-hidden bg-gradient-to-br from-yellow-400 via-yellow-500 to-yellow-600 pt-28 pb-16 dark:from-slate-800 dark:via-slate-900 dark:to-black">
        <div className="relative z-10 mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2">
            <div>
              <p className="text-sm font-semibold uppercase tracking-[0.4em] text-yellow-200 dark:text-yellow-300/70">
                {t('settings.heroBadge')}
              </p>
              <h1 className="mb-4 text-5xl font-black text-white drop-shadow-lg md:text-6xl">
                {t('settings.heroTitle')}
              </h1>
              <p className="mb-6 max-w-2xl text-xl text-yellow-50">
                {t('settings.heroSubtitle')}
              </p>
            </div>
            <div className="relative flex justify-center lg:justify-end">
              <img
                src={airportImage}
                alt="Settings"
                className="h-auto w-full max-w-md rounded-2xl border border-white/20 shadow-2xl"
              />
            </div>
          </div>
        </div>
        <div
          className="absolute bottom-0 left-0 h-24 w-full bg-gradient-to-t from-white/20 to-transparent dark:from-black/40"
          style={{ zIndex: 1 }}
        ></div>
      </section>

      <main className="mx-auto max-w-4xl space-y-10 px-4 pt-16 pb-20 sm:px-6 lg:px-8">
        <section className="space-y-8 rounded-3xl border border-gray-100 bg-white p-8 shadow-lg dark:border-slate-800 dark:bg-slate-900">
          <div>
            <p className="text-sm font-semibold text-gray-500 uppercase tracking-[0.4em] dark:text-slate-400">
              {t('settings.preferences')}
            </p>
            <h2 className="text-2xl font-black text-gray-900 dark:text-slate-100 mt-2">
              {t('settings.language')}
            </h2>
            <p className="text-sm text-gray-500 dark:text-slate-400">
              {t('settings.languageDescription')}
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            {languages.map(option => (
              <button
                key={option.code}
                onClick={() => setLanguage(option.code)}
                className={`rounded-2xl border px-4 py-2 text-sm font-semibold transition ${
                  language === option.code
                    ? 'border-yellow-400 bg-yellow-50 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400 dark:border-yellow-500'
                    : 'border-gray-200 bg-white text-gray-700 hover:border-yellow-200 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300 dark:hover:border-yellow-600'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
          <p className="text-xs text-gray-400 dark:text-slate-500">
            {t('settings.languageNote')}
          </p>

          <div className="border-t border-gray-100 dark:border-slate-800 pt-8">
            <h2 className="text-2xl font-black text-gray-900 dark:text-slate-100">
              {t('settings.theme')}
            </h2>
            <p className="text-sm text-gray-500 dark:text-slate-400">
              {t('settings.themeDescription')}
            </p>
            <div className="mt-4 flex flex-wrap gap-3">
              {['light', 'dark'].map(option => (
                <button
                  key={option}
                  onClick={() => setTheme(option)}
                  className={`rounded-2xl border px-4 py-2 text-sm font-semibold transition ${
                    theme === option
                      ? 'border-yellow-400 bg-yellow-50 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400 dark:border-yellow-500'
                      : 'border-gray-200 bg-white text-gray-700 hover:border-yellow-200 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300 dark:hover:border-yellow-600'
                  }`}
                >
                  {option === 'light'
                    ? t('settings.lightMode')
                    : t('settings.darkMode')}
                </button>
              ))}
            </div>
            <p className="text-xs text-gray-400 mt-2">
              {t('settings.themeNote')}
            </p>
          </div>
        </section>

        <section className="space-y-4 rounded-3xl border border-gray-100 bg-white p-8 shadow-lg dark:border-slate-800 dark:bg-slate-900">
          <div>
            <p className="text-sm font-semibold text-gray-500 uppercase tracking-[0.4em] dark:text-slate-400">
              {t('settings.legal')}
            </p>
            <h2 className="text-2xl font-black text-gray-900 dark:text-slate-100 mt-2">
              {t('settings.policiesTitle')}
            </h2>
            <p className="text-sm text-gray-500 dark:text-slate-400">
              {t('settings.policiesDescription')}
            </p>
          </div>

          <div className="space-y-3">
            <Link
              to="/settings/privacy"
              className="flex items-center justify-between rounded-2xl border border-gray-200 dark:border-slate-700 px-6 py-4 text-left hover:border-yellow-300 dark:hover:border-yellow-600 transition bg-white dark:bg-slate-800"
            >
              <div>
                <p className="text-base font-semibold text-gray-900 dark:text-slate-100">
                  {t('settings.privacyTitle')}
                </p>
                <p className="text-sm text-gray-500 dark:text-slate-400">
                  {t('settings.privacyDescription')}
                </p>
              </div>
              <span className="text-2xl font-bold text-gray-400 dark:text-slate-500">
                ›
              </span>
            </Link>

            <Link
              to="/settings/terms"
              className="flex items-center justify-between rounded-2xl border border-gray-200 dark:border-slate-700 px-6 py-4 text-left hover:border-yellow-300 dark:hover:border-yellow-600 transition bg-white dark:bg-slate-800"
            >
              <div>
                <p className="text-base font-semibold text-gray-900 dark:text-slate-100">
                  {t('settings.termsTitle')}
                </p>
                <p className="text-sm text-gray-500 dark:text-slate-400">
                  {t('settings.termsDescription')}
                </p>
              </div>
              <span className="text-2xl font-bold text-gray-400 dark:text-slate-500">
                ›
              </span>
            </Link>
          </div>
        </section>

        <section className="space-y-4 rounded-3xl border border-gray-100 bg-white p-8 shadow-lg dark:border-slate-800 dark:bg-slate-900">
          <h2 className="text-xl font-black text-gray-900 dark:text-white">
            {t('settings.helpTitle')}
          </h2>
          <p className="text-sm text-gray-600 dark:text-slate-300">
            {t('settings.helpTextBefore')}{' '}
            <a
              href="mailto:contact@flexygroup.ca"
              className="font-semibold text-yellow-600"
            >
              contact@flexygroup.ca
            </a>{' '}
            {t('settings.helpTextAfter')}
          </p>
          <button
            onClick={() => navigate('/chat')}
            className="rounded-2xl bg-black px-5 py-3 text-sm font-semibold text-yellow-400 transition hover:bg-gray-900 dark:bg-yellow-500 dark:text-black dark:hover:bg-yellow-400"
          >
            {t('settings.openSupportChat')}
          </button>
        </section>
      </main>
    </div>
  );
}
